import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { formatAuDate } from "@/lib/date";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Save, Rocket, Copy, Upload, UserPlus, Trash2, ChevronDown, ChevronRight, Files, FileText, Image as ImageIcon, MessageSquare, Calendar as CalendarIcon, Lock, Unlock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { DeleteEventDialog } from "@/components/EventDeletion";
import { AdminShell } from "@/components/AdminShell";
import { enAU } from "date-fns/locale";


const emptySession = () => ({ name: "", start: "", end: "", roles: [], role_descriptions: {}, role_locks: {} });
const emptyDay = (date = "") => ({ date, sessions: [] });

export default function AdminEventEditor() {
  const { eventId } = useParams();
  const isNew = !eventId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [form, setForm] = useState({
    name: "", description: "", contact_email: "", contact_phone: "", phone_public: false, visibility: "public", days: [],
    sms_actions: false,
    sms_confirmation_enabled: false,
    sms_confirmation_template: "Hi {name}, thanks for signing up for {event}. Please confirm your role via the email we just sent: {confirm_link}",
    sms_followup_enabled: false,
    sms_followup_days: 3,
    sms_followup_template: "Hi {name}, we still need you to confirm your volunteer role for {event}. Please click: {confirm_link}",
    sms_provider: "default",
    sms_custom_username: "",
    sms_custom_password: "",
    sms_custom_sender: "",
    sms_twilio_account_sid: "",
    sms_twilio_auth_token: "",
    sms_twilio_from: "",
    email_reply_to: "",
    organised_groups: [],
  });
  const historyRef = useRef([]);
  const [historyDepth, setHistoryDepth] = useState(0);
  const skipNextSnapshot = useRef(false);
  const [newGroup, setNewGroup] = useState("");
  const [status, setStatus] = useState("draft");
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [dateInput, setDateInput] = useState("");
  const [expanded, setExpanded] = useState({});
  const [bulk, setBulk] = useState(null); // {dayIdx, sessIdx}
  const [bulkText, setBulkText] = useState("");
  const [collab, setCollab] = useState({ owner: null, collaborators: [], is_owner: true });
  const [collabEmail, setCollabEmail] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [roleBank, setRoleBank] = useState([]);
  useEffect(() => {
    api.get("/admin/role-bank", { params: { exclude: isNew ? "" : eventId } }).then(({ data }) => setRoleBank(data)).catch(() => {});
  }, [eventId, isNew]);
  const [volunteerCount, setVolunteerCount] = useState(undefined);
  const { user } = useAuth();
  const [disclaimer, setDisclaimer] = useState(null);
  const [promo, setPromo] = useState(null);
  const [cover, setCover] = useState(null);
  const disclaimerRef = useRef(null);
  const promoRef = useRef(null);
  const coverRef = useRef(null);

  const loadEvent = useCallback(async () => {
    if (isNew) return;
    try {
      const { data } = await api.get(`/admin/events/${eventId}`);
      const days = (data.days || []).map((d) => ({
        date: d.date,
        sessions: (d.sessions || []).map((s) => (typeof s === "string"
          ? { name: s, start: "", end: "", location: "", roles: [], role_descriptions: {}, role_locks: {} }
          : { name: s.name, start: s.start || "", end: s.end || "", location: s.location || "", roles: s.roles || [], role_descriptions: { ...(s.role_descriptions || {}) }, role_locks: { ...(s.role_locks || {}) } })),
      }));
      days.sort((a, b) => a.date.localeCompare(b.date));
      setForm({
        name: data.name, description: data.description || "", contact_email: data.contact_email || "",
        contact_phone: data.contact_phone || "", phone_public: !!data.phone_public,
        visibility: data.visibility || "public",
        days,
        sms_actions: !!data.sms_actions,
        sms_confirmation_enabled: !!data.sms_confirmation_enabled,
        sms_confirmation_template: data.sms_confirmation_template || "Hi {name}, thanks for signing up for {event}. Please confirm your role via the email we just sent: {confirm_link}",
        sms_followup_enabled: !!data.sms_followup_enabled,
        sms_followup_days: data.sms_followup_days || 3,
        sms_followup_template: data.sms_followup_template || "Hi {name}, we still need you to confirm your volunteer role for {event}. Please click: {confirm_link}",
        sms_provider: data.sms_provider || "default",
        sms_custom_username: data.sms_custom_username || "",
        sms_custom_password: data.sms_custom_password || "",
        sms_custom_sender: data.sms_custom_sender || "",
        sms_twilio_account_sid: data.sms_twilio_account_sid || "",
        sms_twilio_auth_token: data.sms_twilio_auth_token || "",
        sms_twilio_from: data.sms_twilio_from || "",
        email_reply_to: data.email_reply_to || "",
        organised_groups: data.organised_groups || [],
      });
      historyRef.current = []; setHistoryDepth(0);
      setStatus(data.status); setSlug(data.slug); setVolunteerCount(data.volunteer_count);
      setDisclaimer(data.disclaimer_pdf ? { filename: data.disclaimer_pdf.original_filename, size: data.disclaimer_pdf.size } : null);
      setPromo(data.promo_image ? { filename: data.promo_image.original_filename, size: data.promo_image.size } : null);
      setCover(data.cover_image ? { filename: data.cover_image.original_filename, size: data.cover_image.size } : null);
      const exp = {}; days.forEach((_, i) => { exp[i] = i === 0; });
      setExpanded(exp);
    } catch { toast.error("Failed to load event"); }
  }, [eventId, isNew]);
  const loadCollab = useCallback(async () => {
    if (isNew) return;
    try { const { data } = await api.get(`/admin/events/${eventId}/collaborators`); setCollab(data); }
    catch { /* silent */ }
  }, [eventId, isNew]);
  useEffect(() => { loadEvent(); loadCollab(); }, [loadEvent, loadCollab]);

  // After a first save, land the user on Section 02 (SMS actions) with a brief flash so
  // it is instantly obvious the four extra sections are now unlocked.
  useEffect(() => {
    if (isNew) return;
    if (searchParams.get("justSaved") !== "1") return;
    // Wait a beat for the just-loaded sections to render.
    const t = setTimeout(() => {
      const el = document.getElementById("section-sms");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("volmee-flash");
        setTimeout(() => el.classList.remove("volmee-flash"), 3200);
      }
      // Clean the URL so a manual refresh doesn't re-trigger the flash.
      const next = new URLSearchParams(searchParams);
      next.delete("justSaved");
      setSearchParams(next, { replace: true });
    }, 350);
    return () => clearTimeout(t);
  }, [isNew, searchParams, setSearchParams]);

  function snapshotForm() {
    try {
      historyRef.current.push(JSON.stringify(form));
      if (historyRef.current.length > 25) historyRef.current.shift();
      setHistoryDepth(historyRef.current.length);
    } catch { /* ignore */ }
  }
  function undo() {
    if (!historyRef.current.length) return toast.info("Nothing to undo");
    const prev = historyRef.current.pop();
    setHistoryDepth(historyRef.current.length);
    try {
      const parsed = JSON.parse(prev);
      setForm(parsed);
      toast.success("Undone");
    } catch { toast.error("Undo failed"); }
  }

  function updateDay(i, patch) {
    snapshotForm();
    setForm((f) => { const days = [...f.days]; days[i] = { ...days[i], ...patch }; return { ...f, days }; });
  }
  function updateSession(dayIdx, sIdx, patch) {
    snapshotForm();
    setForm((f) => {
      const days = [...f.days];
      const sessions = [...days[dayIdx].sessions];
      sessions[sIdx] = { ...sessions[sIdx], ...patch };
      days[dayIdx] = { ...days[dayIdx], sessions };
      return { ...f, days };
    });
  }
  const [dateOpen, setDateOpen] = useState(false);
  function addDate() {
    const d = dateInput.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return toast.error("Pick a date first");
    if (form.days.some((x) => x.date === d)) return toast.error("Date already added");
    setForm((f) => ({ ...f, days: [...f.days, emptyDay(d)].sort((a, b) => a.date.localeCompare(b.date)) }));
    setExpanded((e) => ({ ...e, [form.days.length]: true }));
    setDateInput("");
    setDateOpen(false);
  }
  function removeDay(i) {
    if (!window.confirm(`Remove ${form.days[i].date} and all its sessions?`)) return;
    setForm((f) => ({ ...f, days: f.days.filter((_, idx) => idx !== i) }));
  }
  function copyDayFrom(targetIdx, srcIdx) {
    const src = form.days[srcIdx]; if (!src) return;
    updateDay(targetIdx, { sessions: src.sessions.map((s) => ({ ...s, roles: [...s.roles] })) });
    toast.success(`Copied sessions & roles from ${src.date}`);
  }

  function addSession(dayIdx, tmpl) {
    const name = (tmpl.name || "").trim();
    if (!name) return;
    const day = form.days[dayIdx];
    if (day.sessions.some((x) => x.name.toLowerCase() === name.toLowerCase())) return toast.error("Event element already added");
    // IMPORTANT: keep `location` — earlier code dropped this field on Add, so
    // even after the admin typed a suburb it never made it to the DB.
    updateDay(dayIdx, { sessions: [...day.sessions, { name, start: tmpl.start || "", end: tmpl.end || "", location: (tmpl.location || "").trim(), roles: [], role_descriptions: {}, role_locks: {} }] });
  }
  function removeSession(dayIdx, sIdx) {
    const day = form.days[dayIdx];
    updateDay(dayIdx, { sessions: day.sessions.filter((_, i) => i !== sIdx) });
  }
  function moveSession(dayIdx, sIdx, dir) {
    const day = form.days[dayIdx]; const list = [...day.sessions]; const j = sIdx + dir;
    if (j < 0 || j >= list.length) return;
    [list[sIdx], list[j]] = [list[j], list[sIdx]];
    updateDay(dayIdx, { sessions: list });
  }
  function copyRolesFromSession(dayIdx, targetIdx, srcIdx) {
    const src = form.days[dayIdx].sessions[srcIdx]; if (!src) return;
    updateSession(dayIdx, targetIdx, {
      roles: [...src.roles],
      role_descriptions: { ...(src.role_descriptions || {}) },
      role_locks: { ...(src.role_locks || {}) },
    });
    toast.success(`Copied ${src.roles.length} role${src.roles.length !== 1 ? "s" : ""} + descriptions + locks`);
  }

  function addRole(dayIdx, sIdx, r, description, lock) {
    r = (r || "").trim(); if (!r) return;
    const session = form.days[dayIdx].sessions[sIdx];
    if (session.roles.some((x) => x.toLowerCase() === r.toLowerCase())) return toast.error("Role already in this element");
    const nextRoles = [...session.roles, r];
    const nextDesc = { ...(session.role_descriptions || {}) };
    if (description && description.trim()) nextDesc[r] = description.trim();
    const nextLocks = { ...(session.role_locks || {}) };
    if (lock && /^\d{3}$/.test(lock)) nextLocks[r] = lock;
    updateSession(dayIdx, sIdx, { roles: nextRoles, role_descriptions: nextDesc, role_locks: nextLocks });
  }
  function updateRoleDescription(dayIdx, sIdx, role, description) {
    const session = form.days[dayIdx].sessions[sIdx];
    const nextDesc = { ...(session.role_descriptions || {}) };
    if (description && description.trim()) nextDesc[role] = description;
    else delete nextDesc[role];
    updateSession(dayIdx, sIdx, { role_descriptions: nextDesc });
  }
  function updateRoleLock(dayIdx, sIdx, role, code) {
    const session = form.days[dayIdx].sessions[sIdx];
    const nextLocks = { ...(session.role_locks || {}) };
    const digits = (code || "").replace(/\D/g, "").slice(0, 3);
    if (digits) nextLocks[role] = digits;
    else delete nextLocks[role];
    updateSession(dayIdx, sIdx, { role_locks: nextLocks });
  }
  function toggleRoleLock(dayIdx, sIdx, role) {
    const session = form.days[dayIdx].sessions[sIdx];
    const nextLocks = { ...(session.role_locks || {}) };
    if (nextLocks[role] !== undefined) delete nextLocks[role];
    else nextLocks[role] = "";  // enable lock, but no code yet — will be filled by input
    updateSession(dayIdx, sIdx, { role_locks: nextLocks });
  }
  function removeRole(dayIdx, sIdx, rIdx) {
    const session = form.days[dayIdx].sessions[sIdx];
    const removed = session.roles[rIdx];
    const nextDesc = { ...(session.role_descriptions || {}) };
    delete nextDesc[removed];
    const nextLocks = { ...(session.role_locks || {}) };
    delete nextLocks[removed];
    updateSession(dayIdx, sIdx, { roles: session.roles.filter((_, i) => i !== rIdx), role_descriptions: nextDesc, role_locks: nextLocks });
  }
  function copyRoleFromPreviousRole(dayIdx, sIdx, srcRole) {
    if (srcRole.startsWith("bank|")) {
      const b = roleBank[Number(srcRole.split("|")[1])];
      if (!b) return;
      addRole(dayIdx, sIdx, b.role, b.description, "");
      toast.success(`Added "${b.role}"${b.description ? " with copied description" : ""}`);
      return;
    }
    const [dI, sI, name] = srcRole.split("|");
    const src = form.days?.[Number(dI)]?.sessions?.[Number(sI)];
    if (!src) return;
    const desc = (src.role_descriptions || {})[name] || "";
    const lock = (src.role_locks || {})[name] || "";
    addRole(dayIdx, sIdx, name, desc, lock);
    if (lock) toast.success(`Added "${name}" with description + lock code`);
    else if (desc) toast.success(`Added "${name}" with copied description`);
    else toast.success(`Added "${name}"`);
  }
  function moveRole(dayIdx, sIdx, rIdx, dir) {
    const session = form.days[dayIdx].sessions[sIdx];
    const list = [...session.roles]; const j = rIdx + dir;
    if (j < 0 || j >= list.length) return;
    [list[rIdx], list[j]] = [list[j], list[rIdx]];
    updateSession(dayIdx, sIdx, { roles: list });
  }

  function applyBulk() {
    if (!bulk) return;
    const { dayIdx, sessIdx } = bulk;
    const items = bulkText.split(/[\n,;]+/).map((l) => l.trim()).filter(Boolean);
    const session = form.days[dayIdx].sessions[sessIdx];
    const existing = new Set(session.roles.map((r) => r.toLowerCase()));
    const merged = [...session.roles];
    for (const r of items) if (!existing.has(r.toLowerCase())) { existing.add(r.toLowerCase()); merged.push(r); }
    updateSession(dayIdx, sessIdx, { roles: merged });
    toast.success(`Imported ${items.length} role${items.length !== 1 ? "s" : ""}`);
    setBulk(null); setBulkText("");
  }

  // Roles from this event (live, incl. unsaved) + the role bank from the admin's other events
  const allPreviousRoles = [];
  form.days.forEach((d, dI) => {
    d.sessions.forEach((s, sI) => {
      (s.roles || []).forEach((r) => {
        allPreviousRoles.push({
          key: `${dI}|${sI}|${r}`,
          role: r,
          sessName: s.name || `Element ${sI + 1}`,
          date: d.date,
          description: (s.role_descriptions || {})[r] || "",
          group: "This event",
        });
      });
    });
  });
  const thisEventNames = new Set(allPreviousRoles.map((p) => p.role.toLowerCase()));
  roleBank.forEach((b, i) => {
    if (thisEventNames.has(b.role.toLowerCase())) return;
    allPreviousRoles.push({ key: `bank|${i}`, role: b.role, sessName: b.event_name, date: "", description: b.description, group: "Your other events" });
  });

  function addGroup() {
    const g = newGroup.trim();
    if (!g) return;
    if (form.organised_groups.some((x) => x.toLowerCase() === g.toLowerCase())) return toast.error("Group already in the list");
    setForm((f) => ({ ...f, organised_groups: [...f.organised_groups, g] }));
    setNewGroup("");
  }
  function removeGroup(i) {
    setForm((f) => ({ ...f, organised_groups: f.organised_groups.filter((_, idx) => idx !== i) }));
  }

  async function save({ republish = false } = {}) {
    if (!form.name.trim()) return toast.error("Event name required");
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone, phone_public: form.phone_public,
        visibility: form.visibility,
        days: form.days,
        sms_actions: form.sms_actions,
        sms_confirmation_enabled: form.sms_confirmation_enabled,
        sms_confirmation_template: form.sms_confirmation_template,
        sms_followup_enabled: form.sms_followup_enabled,
        sms_followup_days: Number(form.sms_followup_days) || 3,
        sms_followup_template: form.sms_followup_template,
        sms_provider: form.sms_provider || "default",
        sms_custom_username: form.sms_custom_username || "",
        sms_custom_password: form.sms_custom_password || "",
        sms_custom_sender: form.sms_custom_sender || "",
        sms_twilio_account_sid: form.sms_twilio_account_sid || "",
        sms_twilio_auth_token: form.sms_twilio_auth_token || "",
        sms_twilio_from: form.sms_twilio_from || "",
        email_reply_to: form.email_reply_to || "",
        organised_groups: form.organised_groups,
      };
      if (isNew) {
        const { data } = await api.post("/admin/events", payload);
        toast.success("Event created — scroll on to sections 02–05: SMS, Groups, Uploads & Collaborators.", { duration: 6000 });
        historyRef.current = []; setHistoryDepth(0);
        navigate(`/admin/events/${data.id}/edit?justSaved=1`, { replace: true });
      } else {
        try {
          await api.patch(`/admin/events/${eventId}`, payload);
        } catch (e) {
          const d = e.response?.data?.detail;
          if (d && typeof d === "object" && d.requires_force && Array.isArray(d.conflicts)) {
            const preview = d.conflicts.slice(0, 5).map((c) => `• ${c.name} — ${c.assigned.day} · ${c.assigned.session} · ${c.assigned.role}`).join("\n");
            const extra = d.conflicts.length > 5 ? `\n…plus ${d.conflicts.length - 5} more` : "";
            const confirmMsg = `${d.message}\n\nAffected volunteers (${d.conflicts.length}):\n${preview}${extra}\n\nProceed anyway? These volunteers will be moved to the UNALLOCATED pool and appear in the chase list.`;
            if (!window.confirm(confirmMsg)) { setSaving(false); return; }
            const res = await api.patch(`/admin/events/${eventId}?force=true`, payload);
            const moved = res.data?.moved_to_unallocated || 0;
            if (moved > 0) toast.info(`Saved. ${moved} volunteer${moved === 1 ? "" : "s"} moved to Unallocated.`);
          } else { throw e; }
        }
        historyRef.current = []; setHistoryDepth(0);
        if (republish) {
          if (status !== "published") {
            await api.post(`/admin/events/${eventId}/publish`);
            setStatus("published");
          }
          toast.success("Saved & republished — live");
        } else {
          toast.success("Event saved");
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    finally { setSaving(false); }
  }
  async function togglePublish() {
    try {
      const { data } = await api.post(`/admin/events/${eventId}/publish`);
      setStatus(data.status);
      historyRef.current = []; setHistoryDepth(0);
      toast.success(data.status === "published" ? "Event published — public link is live." : "Event unpublished");
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  }
  const copyLink = () => { const url = `${window.location.origin}/events/${slug}`; navigator.clipboard.writeText(url).then(() => toast.success("Copied")); };

  async function addCollab() {
    if (!collabEmail.trim()) return;
    try {
      const { data } = await api.post(`/admin/events/${eventId}/collaborators`, { email: collabEmail.trim() });
      setCollabEmail("");
      if (data.status === "pending") {
        toast.success(`Invitation email sent to ${data.email}. They'll be attached automatically when they sign up.`);
      } else {
        toast.success(`Collaborator added — invitation email sent to ${data.email}`);
      }
      loadCollab();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add collaborator"); }
  }
  async function removeCollab(userId) {
    if (!window.confirm("Remove this collaborator?")) return;
    try { await api.delete(`/admin/events/${eventId}/collaborators/${userId}`); loadCollab(); toast.success("Removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function cancelInvite(inviteId) {
    if (!window.confirm("Cancel this pending invitation?")) return;
    try { await api.delete(`/admin/events/${eventId}/pending-invites/${inviteId}`); loadCollab(); toast.success("Invitation cancelled"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  async function uploadFile(kind, file) {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/events/${eventId}/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "disclaimer") {
        setDisclaimer(data.disclaimer);
        toast.success("Disclaimer uploaded");
      } else if (kind === "cover-image") {
        setCover(data.cover_image);
        toast.success("Cover image uploaded");
      } else {
        setPromo(data.promo_image);
        toast.success("Promo image uploaded");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  }
  async function removeFile(kind) {
    try {
      await api.delete(`/admin/events/${eventId}/${kind}`);
      if (kind === "disclaimer") setDisclaimer(null);
      else if (kind === "cover-image") setCover(null);
      else setPromo(null);
      toast.success("Removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  const canPublish = !isNew && form.days.length && form.days.every((d) => d.sessions.length && d.sessions.every((s) => s.roles.length));

  return (
    <AdminShell
      event={isNew ? null : { id: eventId, name: form.name, status, slug }}
      active="edit"
      title={isNew ? "New event" : "Edit event"}
    >
      <div className="z-20 border-b border-black/10 bg-white/95 backdrop-blur lg:sticky lg:top-14" data-testid="editor-toolbar">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-6 py-3">
          <h1 className="font-display text-2xl font-black tracking-tighter">{isNew ? "New event" : "Edit event"}</h1>
          <div className="flex flex-wrap items-center gap-2">
            {!isNew ? (
              <button onClick={undo} disabled={historyDepth === 0} data-testid="undo-btn"
                className="inline-flex items-center gap-1 border border-black/20 bg-white px-3 py-2 text-xs font-semibold text-black hover:bg-black hover:text-white disabled:opacity-40"
                title={historyDepth ? `Undo (${historyDepth} steps)` : "Nothing to undo"}>
                ↶ Undo{historyDepth ? ` (${historyDepth})` : ""}
              </button>
            ) : null}
            {!isNew ? (
              <button onClick={togglePublish} disabled={!canPublish && status === "draft"} data-testid="publish-btn"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold ${status === "published" ? "border border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-600 hover:text-white" : "brand-btn"} disabled:opacity-40`}>
                <Rocket className="h-3.5 w-3.5" /> {status === "published" ? "Unpublish" : "Publish"}
              </button>
            ) : null}
            {!isNew && status === "published" ? (
              <button onClick={() => save({ republish: true })} disabled={saving} data-testid="save-republish-btn" className="inline-flex items-center gap-1.5 border border-[#002FA7] bg-[#002FA7] px-4 py-2 text-sm font-semibold text-white hover:bg-white hover:text-[#002FA7] disabled:opacity-50">
                <Rocket className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save & Republish"}
              </button>
            ) : null}
            <button onClick={() => save()} disabled={saving} data-testid="save-event-btn" className="inline-flex items-center gap-1.5 border border-black bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-white hover:text-black disabled:opacity-50">
              <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
        <SectionStepper isNew={isNew} />
      </div>

      <main className="mx-auto max-w-4xl px-6 py-10">
        {!isNew ? (
          <div className={`mb-6 flex flex-wrap items-center justify-between gap-3 border ${status === "published" ? "border-emerald-300 bg-emerald-50" : status === "archived" ? "border-amber-400 bg-amber-50" : "border-zinc-300 bg-white"} p-4`}>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest">
                {status === "published" ? <span className="text-emerald-800">Published</span>
                  : status === "archived" ? <span data-testid="editor-status-archived" className="text-amber-800">Archived</span>
                  : <span className="text-zinc-600">Draft</span>}
              </div>
              <div className="mt-1 font-mono text-xs text-zinc-600">Public link: <span className="text-black">/events/{slug}</span></div>
              {status === "archived" ? (
                <div className="mt-2 text-xs text-amber-900" data-testid="editor-archived-hint">This event is archived and hidden from the public. Edit any details below, then click <b>Publish</b> above to bring it back live — no need to restore first.</div>
              ) : null}
            </div>
            {status === "published" ? (
              <div className="flex gap-2">
                <button onClick={copyLink} className="inline-flex items-center gap-1 border border-black/20 bg-white px-3 py-1.5 text-xs hover:bg-black hover:text-white" data-testid="copy-public-link"><Copy className="h-3 w-3" /> Copy link</button>
                <a href={`/events/${slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 border border-black/20 bg-white px-3 py-1.5 text-xs hover:bg-black hover:text-white">Preview</a>
              </div>
            ) : null}
          </div>
        ) : null}

        <div id="section-basics" className="scroll-mt-32 border border-black/10 bg-white p-8 shadow-[8px_8px_0_0_rgba(0,0,0,0.06)]">
          <SectionHeading n="01" label="Event basics" hint="Name, description, visibility and organiser contact." />
          <div className="space-y-6">
            <Field label="Event name">
              <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="input" placeholder="Adelaide Rally 2026" data-testid="event-name-input" />
            </Field>
            <Field label="Description (optional)">
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="input min-h-[80px]" data-testid="event-description-input" />
            </Field>

            {/* Visibility */}
            <div className="border border-black/15 bg-[#F9F9FB] p-4" data-testid="visibility-block">
              <div className="text-xs font-medium uppercase tracking-widest text-[#002FA7]">Visibility</div>
              <div className="mt-3 flex flex-wrap items-start gap-2">
                <label className={`flex-1 min-w-[220px] cursor-pointer border p-3 ${form.visibility === "public" ? "border-[#002FA7] bg-white" : "border-black/10 bg-white hover:border-black/40"}`}>
                  <input type="radio" name="visibility" checked={form.visibility === "public"} onChange={() => setForm((f) => ({ ...f, visibility: "public" }))} className="mr-2 accent-[#002FA7]" data-testid="visibility-public" />
                  <span className="font-semibold text-sm">Public</span>
                  <p className="mt-1 text-[11px] text-zinc-600">Shown as a tile on the VOLMEE home page. Anyone can find + register.</p>
                </label>
                <label className={`flex-1 min-w-[220px] cursor-pointer border p-3 ${form.visibility === "unlisted" ? "border-[#002FA7] bg-white" : "border-black/10 bg-white hover:border-black/40"}`}>
                  <input type="radio" name="visibility" checked={form.visibility === "unlisted"} onChange={() => setForm((f) => ({ ...f, visibility: "unlisted" }))} className="mr-2 accent-[#002FA7]" data-testid="visibility-unlisted" />
                  <span className="font-semibold text-sm">Unlisted</span>
                  <p className="mt-1 text-[11px] text-zinc-600">Not shown on the home page. Only volunteers with the direct link can register.</p>
                </label>
              </div>
            </div>

            {/* Add a day */}
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wider text-zinc-500">Add an event day</div>
              <p className="mb-2 text-xs text-zinc-500">Each day is built independently. Within a day, each <b>event element</b> has its own roles — different elements can carry different roles.</p>
              <div className="flex flex-wrap gap-2">
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="date-picker-btn" className="inline-flex items-center gap-2 border-2 border-[#002FA7] bg-white px-3 py-2 text-sm min-w-[240px]">
                      <CalendarIcon className="h-4 w-4 text-[#002FA7]" />
                      <span className="font-semibold text-[#002FA7]">
                        {dateInput ? formatAuDate(dateInput) : "Pick a date (dd/mm/yyyy)"}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      locale={enAU}
                      selected={dateInput ? new Date(dateInput + "T00:00:00") : undefined}
                      onSelect={(d) => {
                        if (!d) return;
                        const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), da = String(d.getDate()).padStart(2, "0");
                        setDateInput(`${y}-${m}-${da}`);
                      }}
                      weekStartsOn={1}
                    />
                  </PopoverContent>
                </Popover>
                <button type="button" onClick={addDate} data-testid="add-date-btn" className="inline-flex items-center gap-1 border border-black bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black"><Plus className="h-3 w-3" /> Add day</button>
              </div>
            </div>

            {form.days.map((day, i) => (
              <DayCard
                key={`day-${i}`}
                day={day}
                index={i}
                open={!!expanded[i]}
                onToggleOpen={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))}
                onRemove={() => removeDay(i)}
                onAddSession={(s) => addSession(i, s)}
                onRemoveSession={(idx) => removeSession(i, idx)}
                onMoveSession={(idx, dir) => moveSession(i, idx, dir)}
                onUpdateSessionMeta={(idx, patch) => updateSession(i, idx, patch)}
                onCopyRolesFromSession={(target, src) => copyRolesFromSession(i, target, src)}
                onAddRole={(sIdx, r, desc, lock) => addRole(i, sIdx, r, desc, lock)}
                onRemoveRole={(sIdx, rIdx) => removeRole(i, sIdx, rIdx)}
                onMoveRole={(sIdx, rIdx, dir) => moveRole(i, sIdx, rIdx, dir)}
                onUpdateRoleDesc={(sIdx, role, desc) => updateRoleDescription(i, sIdx, role, desc)}
                onToggleRoleLock={(sIdx, role) => toggleRoleLock(i, sIdx, role)}
                onUpdateRoleLockCode={(sIdx, role, code) => updateRoleLock(i, sIdx, role, code)}
                onCopyFromPreviousRole={(sIdx, sourceKey) => copyRoleFromPreviousRole(i, sIdx, sourceKey)}
                allPreviousRoles={allPreviousRoles}
                onBulkRoles={(sIdx) => { setBulk({ dayIdx: i, sessIdx: sIdx }); setBulkText(""); }}
                onCopyFrom={(srcIdx) => copyDayFrom(i, srcIdx)}
                otherDays={form.days.map((d, idx) => ({ idx, date: d.date })).filter((d) => d.idx !== i)}
              />
            ))}
            {!form.days.length && <p className="border border-dashed border-black/20 p-6 text-center text-xs italic text-zinc-500">No days yet. Add one above to get started.</p>}

            {!isNew && !canPublish ? (
              <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">Add at least one event element and one role in every element before you can publish.</div>
            ) : null}

            {/* Organiser contact — placed at bottom of the event editor */}
            <div className="mt-8 border border-black/15 bg-white p-4" data-testid="organiser-contact-block">
              <div className="text-xs font-medium uppercase tracking-widest text-[#002FA7]">Organiser contact</div>
              <p className="mt-1 text-[11px] text-zinc-500">Shown to volunteers on the public event pages so they can reach you.</p>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <Field label="Public contact email">
                  <input type="email" value={form.contact_email || ""} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} className="input" placeholder="organiser@yourclub.org" data-testid="event-contact-email-input" />
                </Field>
                <Field label="Contact phone">
                  <input type="tel" value={form.contact_phone || ""} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} className="input" placeholder="+61 400 000 000" data-testid="event-contact-phone-input" />
                </Field>
              </div>
              <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-xs">
                <input type="checkbox" checked={!!form.phone_public} onChange={(e) => setForm((f) => ({ ...f, phone_public: e.target.checked }))} data-testid="event-phone-public-toggle" className="h-4 w-4 accent-[#002FA7]" />
                <span>Show my phone number publicly on the event page (uncheck to keep it private)</span>
              </label>
            </div>
          </div>
        </div>

        {/* SMS Actions */}
        {!isNew ? (
          <div id="section-sms" className="scroll-mt-32 mt-8 border border-black/10 bg-white p-6" data-testid="sms-actions-section">
            <SectionHeading n="02" label="SMS actions" hint="Automated SMS messages to volunteers." />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-[#002FA7]" />
                <span className="text-sm text-zinc-700">Enable SMS confirmation and follow-up messages for volunteers.</span>
              </div>
              <label className="inline-flex items-center gap-2">
                <span className="text-xs font-mono text-zinc-500" data-testid="sms-actions-status">{form.sms_actions ? "ON" : "OFF"}</span>
                <Switch checked={form.sms_actions} onCheckedChange={(v) => setForm((f) => ({ ...f, sms_actions: !!v }))} data-testid="sms-actions-toggle" />
              </label>
            </div>
            <p className="mt-1 text-xs text-zinc-500">Turn this on to enable a confirmation SMS on registration and/or a follow-up SMS if the volunteer hasn't confirmed by email.</p>

            {/* Reply-to for automated emails */}
            <div className="mt-5 border border-black/15 bg-[#F9F9FB] p-3" data-testid="email-reply-to-block">
              <div className="text-[10px] font-medium uppercase tracking-widest text-[#002FA7]">Email reply-to</div>
              <div className="mt-1 text-xs text-zinc-700">All automated emails still send from <span className="font-mono">no-reply@volmee.app</span>, but volunteer replies land in the inbox below. Leave blank to fall back to the organiser contact email.</div>
              <input
                type="email"
                value={form.email_reply_to}
                onChange={(e) => setForm((f) => ({ ...f, email_reply_to: e.target.value.trim().slice(0, 180) }))}
                placeholder={form.contact_email || "you@your-org.com"}
                className="mt-2 w-full max-w-md border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]"
                data-testid="email-reply-to-input"
              />
            </div>

            {/* SMS provider picker */}
            <div className="mt-5 border-2 border-[#002FA7] bg-[#002FA7]/[0.04] p-3" data-testid="sms-provider-block">
              <div className="text-[10px] font-medium uppercase tracking-widest text-[#002FA7]">SMS provider</div>
              <div className="mt-1 text-xs text-zinc-700">Who sends your SMS. Default uses the VOLMEE platform account; SMS Broadcast or Twilio let you plug in your own account so YOUR account is billed and any sender ID you own is used.</div>
              <div className="mt-2 inline-flex flex-wrap border border-black/20">
                <button type="button" onClick={() => setForm((f) => ({ ...f, sms_provider: "default" }))} data-testid="sms-provider-default"
                  className={`px-4 py-2 text-xs font-semibold ${form.sms_provider !== "custom" && form.sms_provider !== "twilio" ? "bg-[#002FA7] text-white" : "bg-white text-black hover:bg-zinc-100"}`}>1. Use default VOLMEE SMS</button>
                <button type="button" onClick={() => setForm((f) => ({ ...f, sms_provider: "custom" }))} data-testid="sms-provider-custom"
                  className={`border-l border-black/20 px-4 py-2 text-xs font-semibold ${form.sms_provider === "custom" ? "bg-[#002FA7] text-white" : "bg-white text-black hover:bg-zinc-100"}`}>2. My own SMS Broadcast account</button>
                <button type="button" onClick={() => setForm((f) => ({ ...f, sms_provider: "twilio" }))} data-testid="sms-provider-twilio"
                  className={`border-l border-black/20 px-4 py-2 text-xs font-semibold ${form.sms_provider === "twilio" ? "bg-[#002FA7] text-white" : "bg-white text-black hover:bg-zinc-100"}`}>3. My own Twilio account</button>
              </div>
              {form.sms_provider === "custom" ? (
                <div className="mt-3 grid gap-3 md:grid-cols-3" data-testid="sms-custom-fields">
                  <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Username</span>
                    <input value={form.sms_custom_username} onChange={(e) => setForm((f) => ({ ...f, sms_custom_username: e.target.value }))} data-testid="sms-custom-username" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" placeholder="smsbroadcast username" />
                  </label>
                  <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Password / API key</span>
                    <input type="password" value={form.sms_custom_password} onChange={(e) => setForm((f) => ({ ...f, sms_custom_password: e.target.value }))} data-testid="sms-custom-password" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" placeholder="••••••••" />
                  </label>
                  <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Sender ID (optional)</span>
                    <input value={form.sms_custom_sender} onChange={(e) => setForm((f) => ({ ...f, sms_custom_sender: e.target.value.slice(0, 30) }))} data-testid="sms-custom-sender" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" placeholder="e.g. RALLY-SA (11 chars max on most carriers)" />
                  </label>
                  <p className="md:col-span-3 text-[11px] italic text-zinc-500">Get an account at <a href="https://www.smsbroadcast.com.au" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">smsbroadcast.com.au</a>. Credentials are stored server-side and never displayed back after save.</p>
                </div>
              ) : null}
              {form.sms_provider === "twilio" ? (
                <div className="mt-3 grid gap-3 md:grid-cols-3" data-testid="sms-twilio-fields">
                  <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Account SID</span>
                    <input value={form.sms_twilio_account_sid} onChange={(e) => setForm((f) => ({ ...f, sms_twilio_account_sid: e.target.value.trim().slice(0, 64) }))} data-testid="sms-twilio-account-sid" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
                  </label>
                  <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">Auth Token</span>
                    <input type="password" value={form.sms_twilio_auth_token} onChange={(e) => setForm((f) => ({ ...f, sms_twilio_auth_token: e.target.value }))} data-testid="sms-twilio-auth-token" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" placeholder="••••••••" />
                  </label>
                  <label className="text-xs"><span className="mb-1 block font-medium text-zinc-500">From number or Messaging Service SID</span>
                    <input value={form.sms_twilio_from} onChange={(e) => setForm((f) => ({ ...f, sms_twilio_from: e.target.value.trim().slice(0, 30) }))} data-testid="sms-twilio-from" className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" placeholder="+61491570006 or MGxxxxxxxx" />
                  </label>
                  <p className="md:col-span-3 text-[11px] italic text-zinc-500">Get credentials from your <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-[#002FA7] underline">Twilio Console</a>. The "From" field accepts a Twilio phone number in E.164 format (e.g. <span className="font-mono">+61491570006</span>) OR a Messaging Service SID starting with <span className="font-mono">MG</span>. Volunteer mobiles are auto-converted to E.164 for Australian numbers. Trial Twilio accounts can only text verified numbers.</p>
                </div>
              ) : null}
              {!isNew ? <SmsProviderTestPing eventId={eventId} providerLabelHint={form.sms_provider} /> : null}
            </div>

            {form.sms_actions ? (
              <div className="mt-5 space-y-5">
                {/* SMS Confirmation */}
                <div className="border border-black/10 bg-[#F9F9FB] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">SMS Confirmation</div>
                      <div className="text-sm text-zinc-700">Send immediately when a volunteer registers.</div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs">
                      <span className="font-mono text-zinc-500">{form.sms_confirmation_enabled ? "YES" : "NO"}</span>
                      <Switch checked={form.sms_confirmation_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, sms_confirmation_enabled: !!v }))} data-testid="sms-confirmation-toggle" />
                    </label>
                  </div>
                  {form.sms_confirmation_enabled ? (
                    <div className="mt-3">
                      <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-zinc-500">Message (max 480 chars)</label>
                      <textarea value={form.sms_confirmation_template} onChange={(e) => setForm((f) => ({ ...f, sms_confirmation_template: e.target.value.slice(0, 480) }))} className="input min-h-[80px] font-mono text-xs" data-testid="sms-confirmation-template" maxLength={480} />
                      <div className="mt-1 flex justify-between text-[10px] text-zinc-400">
                        <span>Placeholders: <code>{"{name}"}</code>, <code>{"{event}"}</code>, <code>{"{slots}"}</code>, <code>{"{confirm_link}"}</code></span>
                        <span>{form.sms_confirmation_template.length}/480</span>
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* SMS Follow-up */}
                <div className="border border-black/10 bg-[#F9F9FB] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">SMS Follow-up</div>
                      <div className="text-sm text-zinc-700">Send after N days if the volunteer registered but hasn't confirmed via email.</div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs">
                      <span className="font-mono text-zinc-500">{form.sms_followup_enabled ? "YES" : "NO"}</span>
                      <Switch checked={form.sms_followup_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, sms_followup_enabled: !!v }))} data-testid="sms-followup-toggle" />
                    </label>
                  </div>
                  {form.sms_followup_enabled ? (
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-zinc-500">Send follow-up SMS after (days)</label>
                        <input type="number" min="1" max="60" value={form.sms_followup_days} onChange={(e) => setForm((f) => ({ ...f, sms_followup_days: Math.max(1, Math.min(60, Number(e.target.value) || 3)) }))} className="input max-w-[100px]" data-testid="sms-followup-days" />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-zinc-500">Follow-up message (max 480 chars)</label>
                        <textarea value={form.sms_followup_template} onChange={(e) => setForm((f) => ({ ...f, sms_followup_template: e.target.value.slice(0, 480) }))} className="input min-h-[80px] font-mono text-xs" data-testid="sms-followup-template" maxLength={480} />
                        <div className="mt-1 flex justify-between text-[10px] text-zinc-400">
                          <span>Placeholders: <code>{"{name}"}</code>, <code>{"{event}"}</code>, <code>{"{days}"}</code>, <code>{"{confirm_link}"}</code></span>
                          <span>{form.sms_followup_template.length}/480</span>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Organised groups — admin-managed list volunteer picks from */}
        {!isNew ? (
          <div id="section-groups" className="scroll-mt-32 mt-8 border border-black/10 bg-white p-6" data-testid="organised-groups-section">
            <SectionHeading n="03" label="Organised groups" hint="Groups a volunteer can belong to." />
            <p className="text-xs text-zinc-500">Volunteers pick one of these from a dropdown on the register form (e.g. Rotary Club, Lions Club, Local School P&C). Add as many as you like; delete any that don't apply. Leave empty to hide the field entirely.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <input value={newGroup} onChange={(e) => setNewGroup(e.target.value)} className="input flex-1 min-w-[240px]" placeholder="e.g. Rotary Club of Adelaide" data-testid="new-group-input"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addGroup())} />
              <button type="button" onClick={addGroup} data-testid="add-group-btn" className="inline-flex items-center gap-1.5 border border-black bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black"><Plus className="h-3.5 w-3.5" /> Add group</button>
            </div>
            {form.organised_groups.length > 0 ? (
              <ul className="mt-4 flex flex-wrap gap-2" data-testid="organised-groups-list">
                {form.organised_groups.map((g, i) => (
                  <li key={g} data-testid={`group-item-${i}`} className="inline-flex items-center gap-2 border border-black/15 bg-[#F4F4F5] px-3 py-1.5 text-xs">
                    <span className="font-mono">{g}</span>
                    <button onClick={() => removeGroup(i)} data-testid={`group-remove-${i}`} className="text-zinc-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                  </li>
                ))}
              </ul>
            ) : <p className="mt-3 text-xs italic text-zinc-400">No groups yet — the volunteer form will hide the group field.</p>}
          </div>
        ) : null}

        {/* Uploads — disclaimer & promo image */}
        {!isNew ? (
          <div id="section-uploads" className="scroll-mt-32 mt-8">
            <div className="mb-4 border border-black/10 bg-white p-6" data-testid="uploads-section-header">
              <SectionHeading n="04" label="Uploads — Disclaimer PDF, cover image, promo image" hint="Upload a legal disclaimer PDF that volunteers must acknowledge, plus optional event images." />
            </div>
            <div className="grid gap-6 md:grid-cols-3" data-testid="uploads-cards">
            <UploadCard
              icon={<FileText className="h-5 w-5 text-[#002FA7]" />}
              title="Legal disclaimer (PDF)"
              hint="Shown on the register form. Volunteers must tick to acknowledge. Max 5 MB."
              current={disclaimer}
              onPick={() => disclaimerRef.current?.click()}
              onRemove={() => removeFile("disclaimer")}
              previewHref={disclaimer && slug ? `${BACKEND_URL}/api/public/events/${slug}/disclaimer` : null}
              inputRef={disclaimerRef}
              accept="application/pdf"
              onFile={(f) => uploadFile("disclaimer", f)}
              testIdPrefix="disclaimer"
            />
            <UploadCard
              icon={<ImageIcon className="h-5 w-5 text-[#002FA7]" />}
              title="Event cover image (optional)"
              hint="Shown as the event thumbnail on the events dashboard. JPG / PNG / WebP up to 3 MB."
              current={cover}
              onPick={() => coverRef.current?.click()}
              onRemove={() => removeFile("cover-image")}
              previewHref={cover && slug ? `${BACKEND_URL}/api/public/events/${slug}/cover-image` : null}
              inputRef={coverRef}
              accept="image/jpeg,image/png,image/webp,image/gif"
              onFile={(f) => uploadFile("cover-image", f)}
              testIdPrefix="cover"
            />
            <UploadCard
              icon={<ImageIcon className="h-5 w-5 text-[#002FA7]" />}
              title="Promo image (optional)"
              hint="Displayed above the register form as a banner. JPG / PNG / WebP up to 3 MB."
              current={promo}
              onPick={() => promoRef.current?.click()}
              onRemove={() => removeFile("promo-image")}
              previewHref={promo && slug ? `${BACKEND_URL}/api/public/events/${slug}/promo-image` : null}
              inputRef={promoRef}
              accept="image/jpeg,image/png,image/webp,image/gif"
              onFile={(f) => uploadFile("promo-image", f)}
              testIdPrefix="promo"
            />
            </div>
          </div>
        ) : null}

        {/* Collaborators */}
        {!isNew && collab.is_owner ? (
          <div id="section-collab" className="scroll-mt-32 mt-8 border border-black/10 bg-white p-6" data-testid="collaborators-section">
            <SectionHeading n="05" label="Collaborators" hint="Invite others to manage this event." />
            <div className="mt-4 flex flex-wrap gap-2">
              <input value={collabEmail} onChange={(e) => setCollabEmail(e.target.value)} className="input flex-1 min-w-[240px]" placeholder="collaborator@example.com" data-testid="collab-email-input" />
              <button onClick={addCollab} data-testid="add-collab-btn" className="inline-flex items-center gap-1.5 border border-black bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black"><UserPlus className="h-3.5 w-3.5" /> Invite</button>
            </div>
            {collab.collaborators.length > 0 ? (
              <ul className="mt-4 divide-y divide-black/5 border border-black/5">
                {collab.collaborators.map((u) => (
                  <li key={u.id} data-testid={`collab-row-${u.id}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="font-semibold">{u.name}</span>
                    <span className="text-zinc-500">{u.email}</span>
                    <button onClick={() => removeCollab(u.id)} data-testid={`collab-remove-${u.id}`} className="ml-auto inline-flex items-center gap-1 border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-600 hover:text-white"><Trash2 className="h-3 w-3" /> Remove</button>
                  </li>
                ))}
              </ul>
            ) : <p className="mt-3 text-xs italic text-zinc-400">No collaborators yet.</p>}
            {collab.pending_invites && collab.pending_invites.length > 0 ? (
              <div className="mt-6" data-testid="pending-invites-block">
                <div className="text-[10px] font-medium uppercase tracking-widest text-amber-700">Pending invitations</div>
                <ul className="mt-2 divide-y divide-black/5 border border-amber-200 bg-amber-50">
                  {collab.pending_invites.map((inv) => (
                    <li key={inv.id} data-testid={`pending-invite-${inv.id}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <span className="font-mono text-xs">{inv.email}</span>
                      <span className="text-[10px] italic text-amber-800">waiting for sign-up</span>
                      <button onClick={() => cancelInvite(inv.id)} data-testid={`pending-invite-cancel-${inv.id}`} className="ml-auto inline-flex items-center gap-1 border border-red-300 bg-white px-2 py-1 text-xs text-red-700 hover:bg-red-600 hover:text-white"><Trash2 className="h-3 w-3" /> Cancel</button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
        {!isNew && (collab.is_owner || user?.is_super_admin) ? (
          <div className="mt-12 border-2 border-red-200 bg-white p-6" data-testid="danger-zone">
            <div className="text-xs font-black uppercase tracking-widest text-[#FF3B30]">Danger zone</div>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tighter">Delete this event</h2>
            <p className="mt-1 text-sm text-zinc-600">Three steps: confirm, optional backup, then move it to the recycle bin or delete it forever with your authorisation code.</p>
            <button onClick={() => setShowDelete(true)} data-testid="editor-delete-event-btn" className="mt-4 inline-flex items-center gap-1.5 border border-red-600 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-600 hover:text-white">
              <Trash2 className="h-4 w-4" /> Delete event
            </button>
          </div>
        ) : null}
      </main>

      {showDelete ? (
        <DeleteEventDialog
          event={{ id: eventId, name: form.name, slug, status, volunteer_count: volunteerCount }}
          isSuperAdmin={!!(user?.is_super_admin && user?._sa)}
          onClose={() => setShowDelete(false)}
          onDone={({ forever, archived }) => { if (!forever && !archived) toast.success(`"${form.name}" moved to the recycle bin — restore within 24 hours.`); navigate("/admin"); }}
        />
      ) : null}

      {bulk ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" onClick={() => setBulk(null)}>
          <div className="w-full max-w-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">Bulk import roles</div>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tighter">Import roles for this session</h3>
            <p className="mt-2 text-sm text-zinc-600">Paste one role per line, or comma-separated.</p>
            <textarea autoFocus value={bulkText} onChange={(e) => setBulkText(e.target.value)} className="input mt-3 min-h-[220px] font-mono text-sm" data-testid="bulk-textarea"
              placeholder={"Leader\nAssistant\nHelper\nSupport Person\nRoad Official 1\nRoad Official 2"} />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setBulk(null)} className="border border-black/20 px-3 py-1.5 text-xs">Cancel</button>
              <button onClick={applyBulk} data-testid="bulk-apply-btn" className="border border-black bg-black px-3 py-1.5 text-xs font-semibold text-white">Import</button>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .input { width:100%; border:1px solid rgba(0,0,0,0.15); background:white; padding: 10px 12px; font-size: 14px; border-radius: 2px; outline:none; transition: border-color .15s; }
        .input:focus { border-color: #002FA7; box-shadow: 0 0 0 3px rgba(0,47,167,0.12); }
      `}</style>
    </AdminShell>
  );
}

function SectionHeading({ n, label, hint }) {
  return (
    <div className="mb-4 flex items-baseline gap-3 border-b border-black/10 pb-3" data-testid={`section-heading-${n}`}>
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center border-2 border-[#002FA7] bg-[#002FA7] font-display text-sm font-black text-white">{n}</span>
      <div className="flex-1">
        <h3 className="font-display text-xl font-black tracking-tighter">{label}</h3>
        {hint ? <p className="mt-0.5 text-xs text-zinc-500">{hint}</p> : null}
      </div>
    </div>
  );
}

function SectionStepper({ isNew }) {
  const items = isNew
    ? [{ id: "section-basics", n: "01", label: "Event basics" }]
    : [
        { id: "section-basics", n: "01", label: "Event basics + Days" },
        { id: "section-sms", n: "02", label: "SMS actions" },
        { id: "section-groups", n: "03", label: "Groups" },
        { id: "section-uploads", n: "04", label: "Uploads (PDF & Images)" },
        { id: "section-collab", n: "05", label: "Collaborators" },
      ];
  function jump(id) {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  if (isNew) return null;
  return (
    <div className="border-t border-black/5 bg-[#F9F9FB]" data-testid="section-stepper">
      <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-1 px-6 py-2">
        <span className="pr-2 text-[10px] font-medium uppercase tracking-widest text-zinc-500">Jump to</span>
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => jump(it.id)}
            data-testid={`stepper-jump-${it.n}`}
            className="inline-flex items-center gap-1.5 border border-black/15 bg-white px-2 py-1 text-[11px] font-semibold text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7]"
          >
            <span className="font-display font-black text-[#002FA7]">{it.n}</span>
            <span>{it.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return <label className="block"><span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">{label}</span>{children}</label>;
}

function UploadCard({ icon, title, hint, current, onPick, onRemove, previewHref, inputRef, accept, onFile, testIdPrefix }) {
  return (
    <div className="border border-black/10 bg-white p-5" data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-center gap-2">
        {icon}
        <div className="font-display text-lg font-black tracking-tighter">{title}</div>
      </div>
      <p className="mt-1 text-xs text-zinc-500">{hint}</p>
      {current ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 border border-black/10 bg-[#F9F9FB] p-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold" data-testid={`${testIdPrefix}-filename`}>{current.filename}</div>
            {current.size ? <div className="font-mono text-[11px] text-zinc-500">{(current.size / 1024).toFixed(0)} KB</div> : null}
          </div>
          {previewHref ? <a href={previewHref} target="_blank" rel="noreferrer" className="border border-black/20 px-2 py-1 text-xs hover:bg-black hover:text-white" data-testid={`${testIdPrefix}-preview`}>Preview</a> : null}
          <button onClick={onRemove} className="inline-flex items-center gap-1 border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-600 hover:text-white" data-testid={`${testIdPrefix}-remove-btn`}><Trash2 className="h-3 w-3" /> Remove</button>
        </div>
      ) : (
        <div className="mt-4">
          <button onClick={onPick} className="inline-flex items-center gap-2 border border-black bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black" data-testid={`${testIdPrefix}-upload-btn`}>
            <Upload className="h-3.5 w-3.5" /> Upload
          </button>
          <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { onFile(f); e.target.value = ""; } }} />
        </div>
      )}
    </div>
  );
}

function DayCard({ day, index, open, onToggleOpen, onRemove, onAddSession, onRemoveSession, onMoveSession, onUpdateSessionMeta, onCopyRolesFromSession, onAddRole, onRemoveRole, onMoveRole, onUpdateRoleDesc, onToggleRoleLock, onUpdateRoleLockCode, onCopyFromPreviousRole, allPreviousRoles, onBulkRoles, onCopyFrom, otherDays }) {
  const [sessionInput, setSessionInput] = useState({ name: "", start: "", end: "", location: "" });
  const [copyFromIdx, setCopyFromIdx] = useState("");

  return (
    <div className="border border-black/15 bg-white" data-testid={`day-card-${index}`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-black/5 bg-[#F9F9FB] px-4 py-3">
        <button onClick={onToggleOpen} data-testid={`day-toggle-${index}`} className="inline-flex items-center gap-1.5 font-display text-base font-black tracking-tighter">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {formatAuDate(day.date)}
        </button>
        <span className="font-mono text-[11px] text-zinc-500">{day.date}</span>
        <span className="ml-2 border border-black/10 bg-white px-2 py-0.5 text-[10px] font-mono text-zinc-600">
          {day.sessions.length} element{day.sessions.length !== 1 ? "s" : ""} · {day.sessions.reduce((a, s) => a + s.roles.length, 0)} roles
        </span>
        <div className="ml-auto flex items-center gap-2">
          {otherDays.length > 0 ? (
            <div className="flex items-center gap-1">
              <select value={copyFromIdx} onChange={(e) => setCopyFromIdx(e.target.value)} className="border border-black/15 bg-white px-2 py-1 text-xs" data-testid={`copy-from-select-${index}`}>
                <option value="">Copy from…</option>
                {otherDays.map((o) => <option key={o.idx} value={o.idx}>{o.date}</option>)}
              </select>
              <button onClick={() => { if (copyFromIdx !== "") { onCopyFrom(Number(copyFromIdx)); setCopyFromIdx(""); } }} data-testid={`copy-from-btn-${index}`} className="inline-flex items-center gap-1 border border-black/20 bg-white px-2 py-1 text-xs hover:bg-black hover:text-white disabled:opacity-40" disabled={copyFromIdx === ""}>
                <Files className="h-3 w-3" /> Copy
              </button>
            </div>
          ) : null}
          <button onClick={onRemove} data-testid={`day-remove-${index}`} className="inline-flex items-center gap-1 border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-600 hover:text-white"><X className="h-3 w-3" /> Day</button>
        </div>
      </div>

      {open ? (
        <div className="p-4 md:p-6 space-y-5">
          {/* Add an event element */}
          <div className="border border-dashed border-black/20 p-4">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Add an event element</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input value={sessionInput.name} onChange={(e) => setSessionInput((s) => ({ ...s, name: e.target.value }))} className="input flex-1 min-w-[180px]" placeholder="Event element name (e.g. Stage 1)" data-testid={`session-name-input-${index}`} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), (onAddSession(sessionInput), setSessionInput({ name: "", start: "", end: "", location: "" })))} />
              <input type="time" value={sessionInput.start} onChange={(e) => setSessionInput((s) => ({ ...s, start: e.target.value }))} className="input max-w-[130px]" data-testid={`session-start-input-${index}`} />
              <input type="time" value={sessionInput.end} onChange={(e) => setSessionInput((s) => ({ ...s, end: e.target.value }))} className="input max-w-[130px]" data-testid={`session-end-input-${index}`} />
              <input value={sessionInput.location || ""} onChange={(e) => setSessionInput((s) => ({ ...s, location: e.target.value }))} className="input flex-1 min-w-[160px]" placeholder="Location (suburb e.g. Norwood)" data-testid={`session-location-input-${index}`} />
              <button type="button" onClick={() => { onAddSession(sessionInput); setSessionInput({ name: "", start: "", end: "", location: "" }); }} data-testid={`add-session-btn-${index}`} className="inline-flex items-center gap-1 border border-black bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black"><Plus className="h-3 w-3" /> Add element</button>
            </div>
          </div>

          {day.sessions.map((s, si) => (
            <SessionCard
              key={`session-${index}-${si}`}
              index={si}
              dayIndex={index}
              session={s}
              otherSessions={day.sessions.map((x, xi) => ({ idx: xi, name: x.name })).filter((x) => x.idx !== si)}
              allPreviousRoles={allPreviousRoles.filter((p) => !(p.role && s.roles.some((r) => r.toLowerCase() === p.role.toLowerCase())))}
              onUpdateMeta={(patch) => onUpdateSessionMeta(si, patch)}
              onRemove={() => onRemoveSession(si)}
              onMove={(dir) => onMoveSession(si, dir)}
              onCopyRolesFromSession={(srcIdx) => onCopyRolesFromSession(si, srcIdx)}
              onAddRole={(r, desc, lock) => onAddRole(si, r, desc, lock)}
              onRemoveRole={(rIdx) => onRemoveRole(si, rIdx)}
              onMoveRole={(rIdx, dir) => onMoveRole(si, rIdx, dir)}
              onUpdateRoleDesc={(role, desc) => onUpdateRoleDesc(si, role, desc)}
              onToggleRoleLock={(role) => onToggleRoleLock(si, role)}
              onUpdateRoleLockCode={(role, code) => onUpdateRoleLockCode(si, role, code)}
              onCopyFromPreviousRole={(sourceKey) => onCopyFromPreviousRole(si, sourceKey)}
              onBulkRoles={() => onBulkRoles(si)}
            />
          ))}
          {!day.sessions.length && <p className="border border-dashed border-black/20 p-4 text-center text-xs italic text-zinc-500">No event elements on this day yet.</p>}
        </div>
      ) : null}
    </div>
  );
}

function SessionCard({ index, dayIndex, session, otherSessions, allPreviousRoles, onUpdateMeta, onRemove, onMove, onCopyRolesFromSession, onAddRole, onRemoveRole, onMoveRole, onUpdateRoleDesc, onToggleRoleLock, onUpdateRoleLockCode, onCopyFromPreviousRole, onBulkRoles }) {
  const [roleInput, setRoleInput] = useState("");
  const [roleDescInput, setRoleDescInput] = useState("");
  const [roleLockOn, setRoleLockOn] = useState(false);
  const [roleLockCode, setRoleLockCode] = useState("");
  const [copyFromIdx, setCopyFromIdx] = useState("");
  const [copyFromPrevKey, setCopyFromPrevKey] = useState("");

  function submitRole() {
    if (!roleInput.trim()) return;
    if (roleLockOn && !/^\d{3}$/.test(roleLockCode)) {
      toast.error("Locked roles need a 3-digit unlock code"); return;
    }
    onAddRole(roleInput, roleDescInput, roleLockOn ? roleLockCode : "");
    setRoleInput(""); setRoleDescInput(""); setRoleLockOn(false); setRoleLockCode("");
  }

  const roleDescriptions = session.role_descriptions || {};
  const roleLocks = session.role_locks || {};
  return (
    <div className="border border-black/15" data-testid={`session-card-${dayIndex}-${index}`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-black/5 bg-[#F4F4F5] px-3 py-2 text-xs">
        <button onClick={() => onMove(-1)} className="text-zinc-400 hover:text-black">↑</button>
        <button onClick={() => onMove(1)} className="text-zinc-400 hover:text-black">↓</button>
        <input value={session.name} onChange={(e) => onUpdateMeta({ name: e.target.value })} className="border border-black/10 bg-white px-2 py-1 text-xs font-semibold min-w-[160px]" data-testid={`session-name-${dayIndex}-${index}`} />
        <input type="time" value={session.start} onChange={(e) => onUpdateMeta({ start: e.target.value })} className="border border-black/10 bg-white px-2 py-1 text-xs" />
        <span>–</span>
        <input type="time" value={session.end} onChange={(e) => onUpdateMeta({ end: e.target.value })} className="border border-black/10 bg-white px-2 py-1 text-xs" />
        <input value={session.location || ""} onChange={(e) => onUpdateMeta({ location: e.target.value })} placeholder="Location (suburb e.g. Norwood)" data-testid={`session-location-${dayIndex}-${index}`} className="border border-black/10 bg-white px-2 py-1 text-xs min-w-[220px]" />
        <span className="ml-auto text-[10px] font-mono text-zinc-500">{session.roles.length} role{session.roles.length !== 1 ? "s" : ""}</span>
        <button onClick={onRemove} data-testid={`session-remove-${dayIndex}-${index}`} className="inline-flex items-center gap-1 border border-red-300 px-2 py-1 text-[10px] text-red-700 hover:bg-red-600 hover:text-white"><X className="h-3 w-3" /> Element</button>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-xs font-medium uppercase tracking-wider text-zinc-500">Roles for this event element</span>
          <div className="flex flex-wrap items-center gap-2">
            {otherSessions.length > 0 ? (
              <div className="flex items-center gap-1">
                <select value={copyFromIdx} onChange={(e) => setCopyFromIdx(e.target.value)} className="border border-black/15 bg-white px-2 py-1 text-[11px]" data-testid={`copy-roles-select-${dayIndex}-${index}`}>
                  <option value="">Copy all roles from…</option>
                  {otherSessions.map((o) => <option key={o.idx} value={o.idx}>{o.name || `Element ${o.idx + 1}`}</option>)}
                </select>
                <button onClick={() => { if (copyFromIdx !== "") { onCopyRolesFromSession(Number(copyFromIdx)); setCopyFromIdx(""); } }} data-testid={`copy-roles-btn-${dayIndex}-${index}`} className="inline-flex items-center gap-1 border border-black/20 bg-white px-2 py-1 text-[11px] hover:bg-black hover:text-white disabled:opacity-40" disabled={copyFromIdx === ""}>
                  <Files className="h-3 w-3" /> Copy
                </button>
              </div>
            ) : null}
            <button type="button" onClick={onBulkRoles} data-testid={`bulk-roles-${dayIndex}-${index}`} className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] underline"><Upload className="h-3 w-3" /> Bulk import</button>
          </div>
        </div>

        {/* Copy a single role (with description) from any previously created role in this event */}
        {(allPreviousRoles || []).length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 border border-dashed border-[#002FA7]/40 bg-[#002FA7]/[0.03] p-2">
            <span className="text-[10px] font-medium uppercase tracking-widest text-[#002FA7]">Copy a role from a previously-created one</span>
            <select value={copyFromPrevKey} onChange={(e) => setCopyFromPrevKey(e.target.value)} className="border border-black/15 bg-white px-2 py-1 text-[11px] min-w-[240px]" data-testid={`copy-prev-role-select-${dayIndex}-${index}`}>
              <option value="">Pick a role to copy…</option>
              {["This event", "Your other events"].map((g) => {
                const items = allPreviousRoles.filter((p) => p.group === g);
                return items.length ? (
                  <optgroup key={g} label={g}>
                    {items.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.role} — {p.date ? `${p.date.split("-").reverse().join("/")} · ` : ""}{p.sessName}{p.description ? " (has description)" : ""}
                      </option>
                    ))}
                  </optgroup>
                ) : null;
              })}
            </select>
            <button onClick={() => { if (copyFromPrevKey) { onCopyFromPreviousRole(copyFromPrevKey); setCopyFromPrevKey(""); } }} data-testid={`copy-prev-role-btn-${dayIndex}-${index}`} className="inline-flex items-center gap-1 border border-[#002FA7] bg-white px-2 py-1 text-[11px] font-semibold text-[#002FA7] hover:bg-[#002FA7] hover:text-white disabled:opacity-40" disabled={!copyFromPrevKey}>
              <Copy className="h-3 w-3" /> Copy role
            </button>
          </div>
        ) : null}

        {/* Add role */}
        <div className="mt-3 border border-black/10 bg-[#F9F9FB] p-3">
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-widest text-zinc-500">Role name</label>
          <input value={roleInput} onChange={(e) => setRoleInput(e.target.value)} className="input w-full" placeholder="e.g. Road Official 1" data-testid={`role-input-${dayIndex}-${index}`} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), submitRole())} />
          <div className="mt-2">
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-widest text-zinc-500">Role description (target ≈ 50 words — shown to volunteers)</label>
            <textarea value={roleDescInput} onChange={(e) => setRoleDescInput(e.target.value.slice(0, 800))} className="input min-h-[70px] text-xs" placeholder="Explain what this role actually involves — where they'll be, what they'll be doing, and any expectations. Volunteers see this via an info icon." data-testid={`role-desc-input-${dayIndex}-${index}`} />
            <WordCount text={roleDescInput} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-black/10 pt-2">
            <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#8B4513]">
              <input type="checkbox" checked={roleLockOn} onChange={(e) => setRoleLockOn(e.target.checked)} className="h-3.5 w-3.5 accent-[#8B4513]" data-testid={`role-lock-toggle-${dayIndex}-${index}`} />
              <Lock className="h-3.5 w-3.5" /> Lock this role (require a 3-digit code)
            </label>
            {roleLockOn ? (
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{3}"
                maxLength={3}
                value={roleLockCode}
                onChange={(e) => setRoleLockCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                className="input max-w-[100px] text-center font-mono tracking-widest text-sm"
                placeholder="•••"
                data-testid={`role-lock-code-input-${dayIndex}-${index}`}
              />
            ) : null}
          </div>
          {/* Add role button — placed at bottom of the section so admin can finish name+description+lock before clicking */}
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={submitRole} data-testid={`add-role-btn-${dayIndex}-${index}`} disabled={!roleInput.trim() || (roleLockOn && !/^\d{3}$/.test(roleLockCode))} className="inline-flex items-center gap-1 border border-black bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black disabled:opacity-40">
              <Plus className="h-3 w-3" /> Add role
            </button>
          </div>
        </div>

        <ul className="mt-3 space-y-2">
          {session.roles.map((r, ri) => {
            const lockCode = roleLocks[r];
            const isLocked = lockCode !== undefined;
            return (
              <li key={`role-${dayIndex}-${index}-${ri}`} data-testid={`role-item-${dayIndex}-${index}-${ri}`} className={`border ${isLocked ? "border-[#8B4513]/50 bg-[#8B4513]/[0.03]" : "border-black/15 bg-white"} p-3`}>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => onMoveRole(ri, -1)} className="text-zinc-400 hover:text-black">↑</button>
                  <button onClick={() => onMoveRole(ri, 1)} className="text-zinc-400 hover:text-black">↓</button>
                  {isLocked ? <Lock className="h-4 w-4 text-[#8B4513]" data-testid={`role-locked-icon-${dayIndex}-${index}-${ri}`} /> : null}
                  <span className="font-mono text-sm font-semibold">{r}</span>
                  <label className="ml-3 inline-flex items-center gap-1 text-[11px] font-medium text-[#8B4513]">
                    <input
                      type="checkbox"
                      checked={isLocked}
                      onChange={() => onToggleRoleLock(r)}
                      className="h-3 w-3 accent-[#8B4513]"
                      data-testid={`role-lock-checkbox-${dayIndex}-${index}-${ri}`}
                    />
                    Locked
                  </label>
                  {isLocked ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#5A2E0C]">
                      Code:
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d{3}"
                        maxLength={3}
                        value={lockCode}
                        onChange={(e) => onUpdateRoleLockCode(r, e.target.value)}
                        className="border border-[#8B4513]/40 bg-white px-2 py-1 text-center font-mono text-sm tracking-widest"
                        placeholder="•••"
                        data-testid={`role-lock-code-${dayIndex}-${index}-${ri}`}
                        style={{ width: 60 }}
                      />
                      {/^\d{3}$/.test(lockCode || "") ? (
                        <span className="text-[10px] text-emerald-700">✓</span>
                      ) : (
                        <span className="text-[10px] text-amber-700">3 digits required</span>
                      )}
                    </span>
                  ) : null}
                  <button onClick={() => onRemoveRole(ri)} data-testid={`role-remove-${dayIndex}-${index}-${ri}`} className="ml-auto inline-flex items-center gap-1 border border-red-300 px-2 py-1 text-[10px] text-red-700 hover:bg-red-600 hover:text-white"><X className="h-3 w-3" /> Remove</button>
                </div>
                <div className="mt-2">
                  <label className="mb-1 block text-[10px] font-medium uppercase tracking-widest text-zinc-500">Role description (≈ 50 words)</label>
                  <textarea
                    value={roleDescriptions[r] || ""}
                    onChange={(e) => onUpdateRoleDesc(r, e.target.value.slice(0, 800))}
                    className="input min-h-[60px] text-xs"
                    placeholder="Describe what the volunteer does in this role. Kept as guidance for both admins and volunteers."
                    data-testid={`role-desc-${dayIndex}-${index}-${ri}`}
                  />
                  <WordCount text={roleDescriptions[r] || ""} />
                </div>
              </li>
            );
          })}
          {!session.roles.length && <li className="text-xs italic text-zinc-400">No roles yet. Add one above.</li>}
        </ul>
      </div>
    </div>
  );
}

function WordCount({ text }) {
  const words = (text || "").trim() ? text.trim().split(/\s+/).length : 0;
  const over = words > 50;
  return (
    <div className={`mt-1 flex justify-between text-[10px] ${over ? "text-amber-700" : "text-zinc-400"}`}>
      <span>{over ? `⚠ ${words} words — over the 50-word guideline, but you can still save` : "Guideline: keep this concise (≤ 50 words)"}</span>
      <span data-testid="role-desc-wordcount">{words}/50 words</span>
    </div>
  );
}


function SmsProviderTestPing({ eventId, providerLabelHint }) {
  const [mobile, setMobile] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const providerName = providerLabelHint === "twilio" ? "Twilio" : providerLabelHint === "custom" ? "your SMS Broadcast account" : "the default VOLMEE SMS account";

  async function sendTest() {
    if (!mobile.trim()) {
      toast.error("Enter a mobile number first (e.g. 0412345678)");
      return;
    }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/admin/events/${eventId}/sms/test`, {
        mobile: mobile.trim(),
        message: message.trim(),
      });
      setResult(data);
      if (data.ok) toast.success(`Test SMS sent via ${data.provider}. Check your phone.`);
      else toast.error(`Test SMS failed: ${data.provider_response || "unknown error"}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setResult({ ok: false, provider_response: msg });
      toast.error(`Test failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-dashed border-[#002FA7]/30 pt-3" data-testid="sms-provider-test-block">
      <div className="text-[10px] font-medium uppercase tracking-widest text-[#002FA7]">Test this provider</div>
      <p className="mt-1 text-xs text-zinc-700">
        Save first, then send a real SMS to your own phone via <strong>{providerName}</strong>. This is the fastest way to prove your BYOK credentials work before a volunteer registers.
      </p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="text-xs">
          <span className="mb-1 block font-medium text-zinc-500">Your mobile (Australian 10-digit or E.164)</span>
          <input
            value={mobile}
            onChange={(e) => setMobile(e.target.value.slice(0, 20))}
            data-testid="sms-test-mobile"
            className="w-56 border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]"
            placeholder="0412345678"
          />
        </label>
        <label className="flex-1 text-xs">
          <span className="mb-1 block font-medium text-zinc-500">Custom message (optional)</span>
          <input
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 160))}
            data-testid="sms-test-message"
            className="w-full border border-black/20 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]"
            placeholder="Defaults to a VOLMEE test ping."
          />
        </label>
        <button
          type="button"
          onClick={sendTest}
          disabled={busy || !mobile.trim()}
          data-testid="sms-test-send-btn"
          className="brand-btn px-4 py-2 text-xs disabled:opacity-50"
        >
          {busy ? "Sending…" : "Send test SMS"}
        </button>
      </div>
      {result ? (
        <div
          data-testid="sms-test-result"
          className={`mt-3 border px-3 py-2 text-xs ${result.ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-red-300 bg-red-50 text-red-900"}`}
        >
          <div className="font-semibold">
            {result.ok ? "Provider responded OK" : "Provider returned an error"} — via {result.provider || "-"}
          </div>
          {result.provider_response ? (
            <div className="mt-1 font-mono text-[11px] whitespace-pre-wrap break-all">{result.provider_response}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
